// Generic indented-tree renderer for parent/child relationships.
// Used for: Account hierarchy (parent → children) and Contact "reports to" org chart.
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, User, ChevronRight } from "lucide-react";

interface Node {
  id: string;
  label: string;
  sublabel?: string;
  href: string;
  children?: Node[];
}

function renderNode(node: Node, depth: number, currentId?: string, icon?: React.ReactNode): React.ReactNode {
  const isCurrent = node.id === currentId;
  return (
    <li key={node.id}>
      <div
        className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
          isCurrent ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted"
        }`}
        style={{ paddingLeft: 8 + depth * 16 }}
      >
        {depth > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
        {icon}
        <Link to={node.href} className="hover:underline">{node.label}</Link>
        {node.sublabel && <span className="ml-1 text-xs text-muted-foreground">{node.sublabel}</span>}
      </div>
      {node.children && node.children.length > 0 && (
        <ul>{node.children.map((c) => renderNode(c, depth + 1, currentId, icon))}</ul>
      )}
    </li>
  );
}

// ----------------------------------------------------------------------------
// Account hierarchy
// ----------------------------------------------------------------------------

export function AccountHierarchyPanel({ accountId }: { accountId: string }) {
  // Pull all accounts once + build the tree client-side. Fine for a few thousand;
  // for very large orgs swap to a dedicated /accounts/:id/tree endpoint.
  const { data, isLoading } = useQuery({
    queryKey: ["/accounts/all-for-tree"],
    queryFn: async () => (await api.get("/accounts", { params: { pageSize: 1000 } })).data.data as any[],
  });

  if (isLoading) return <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>;
  const accounts = data ?? [];
  const me = accounts.find((a) => a.id === accountId);
  if (!me) return <p className="py-6 text-center text-sm text-muted-foreground">Account not found.</p>;

  // Walk up to root.
  const ancestors: any[] = [];
  let cursor = me;
  const seen = new Set<string>();
  while (cursor.parentId && !seen.has(cursor.parentId)) {
    seen.add(cursor.parentId);
    const parent = accounts.find((a) => a.id === cursor.parentId);
    if (!parent) break;
    ancestors.unshift(parent);
    cursor = parent;
  }
  const root = ancestors[0] ?? me;

  // Build the subtree from root.
  function build(a: any): Node {
    const children = accounts.filter((x) => x.parentId === a.id);
    return {
      id: a.id,
      label: a.name,
      sublabel: a.industry ?? a.type,
      href: `/accounts/${a.id}`,
      children: children.map(build),
    };
  }

  const tree = build(root);
  const childCount = accounts.filter((a) => a.parentId === accountId).length;
  const ancestorCount = ancestors.length;

  return (
    <div className="space-y-3">
      <div className="text-sm text-muted-foreground">
        {ancestorCount > 0 && <>Showing {ancestorCount} ancestor{ancestorCount === 1 ? "" : "s"}. </>}
        {childCount > 0 && <>{childCount} direct subsidiar{childCount === 1 ? "y" : "ies"}.</>}
        {ancestorCount === 0 && childCount === 0 && "This account is standalone — no parent or subsidiaries."}
      </div>
      <Card>
        <CardContent className="p-3">
          <ul>{renderNode(tree, 0, accountId, <Building2 className="h-4 w-4 text-muted-foreground" />)}</ul>
        </CardContent>
      </Card>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Contact "reports to" org chart
// ----------------------------------------------------------------------------

export function ReportsToTreePanel({ contactId }: { contactId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["/contacts/all-for-tree"],
    queryFn: async () => (await api.get("/contacts", { params: { pageSize: 1000 } })).data.data as any[],
  });

  if (isLoading) return <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>;
  const contacts = data ?? [];
  const me = contacts.find((c) => c.id === contactId);
  if (!me) return <p className="py-6 text-center text-sm text-muted-foreground">Contact not found.</p>;

  // Walk up reports-to chain.
  const ancestors: any[] = [];
  let cursor = me;
  const seen = new Set<string>();
  while (cursor.reportsToId && !seen.has(cursor.reportsToId)) {
    seen.add(cursor.reportsToId);
    const boss = contacts.find((c) => c.id === cursor.reportsToId);
    if (!boss) break;
    ancestors.unshift(boss);
    cursor = boss;
  }
  const root = ancestors[0] ?? me;

  function build(c: any): Node {
    const reports = contacts.filter((x) => x.reportsToId === c.id);
    return {
      id: c.id,
      label: c.name,
      sublabel: c.title ?? c.email,
      href: `/contacts/${c.id}`,
      children: reports.map(build),
    };
  }

  const tree = build(root);
  const directReports = contacts.filter((c) => c.reportsToId === contactId).length;

  return (
    <div className="space-y-3">
      <div className="text-sm text-muted-foreground">
        {ancestors.length > 0 && <>Reports up through {ancestors.length} level{ancestors.length === 1 ? "" : "s"}. </>}
        {directReports > 0 && <>{directReports} direct report{directReports === 1 ? "" : "s"}.</>}
        {ancestors.length === 0 && directReports === 0 && "No org-chart relationships set."}
      </div>
      <Card>
        <CardContent className="p-3">
          <ul>{renderNode(tree, 0, contactId, <User className="h-4 w-4 text-muted-foreground" />)}</ul>
        </CardContent>
      </Card>
    </div>
  );
}
